import { API } from '../../../../../../api';
import { ElliottSttCallbacks, ElliottSttEngine, ElliottTtsEngine, remainingText, splitIntoChunks } from './voiceEngine';

// Server-backed speech engines (the BE proxy /elliott-voice/*: Elliott's real
// voice, and Whisper-family STT that understands accented English far better
// than the browser recognizer). Same contracts as the browser adapters — the
// hook cascades server → browser → text.
//
// Availability is feature-detected, but carefully: the browser voice is the
// one thing Elliott must never sound like, so the session only latches onto
// it when the server voice is truly not there (503 = no key, 404 = not
// deployed) or after two consecutive turns in which not a single chunk became
// audible. A timeout, a 5xx, a decode or autoplay hiccup on one chunk hands
// the REST of that turn to the browser voice and tries the server again next
// turn. No config needed on the FE.

let serverVoiceDown = false;
export const isServerVoiceDown = () => serverVoiceDown;
export const markServerVoiceDown = () => {
  serverVoiceDown = true;
};

// Turns in a row that produced no audible chunk at all (reset by any chunk
// that plays). Two of those and the server voice is considered down.
let silentTurns = 0;
const SILENT_TURNS_BEFORE_LATCH = 2;

/** "The voice is not there": no point retrying, no point trying next turn. */
const isVoiceUnavailable = (err: any): boolean => {
  const status = err?.response?.status;
  return status === 503 || status === 404;
};

// One sentence-sized chunk; a transient failure (timeout, 5xx, blip, empty
// body) gets one retry, an unavailable answer none.
const fetchChunk = (chunk: string, attempt = 0): Promise<Blob> =>
  API.elliottVoiceTts({ text: chunk })
    .then((res: any) => {
      const b64: string = res?.data?.audioBase64 || '';
      if (!b64) throw new Error('empty audio');
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      return new Blob([bytes], { type: res?.data?.mime || 'audio/mpeg' });
    })
    .catch((err: any) => {
      if (attempt < 1 && !isVoiceUnavailable(err)) return fetchChunk(chunk, attempt + 1);
      throw err;
    });

// ── Server TTS ───────────────────────────────────────────────────────────────

// One shared AudioContext for the amplitude meter (browsers cap instances).
let sharedAudioCtx: any = null;
const getAudioCtx = (): any => {
  if (!sharedAudioCtx) {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    sharedAudioCtx = new AC();
  }
  try {
    sharedAudioCtx.resume();
  } catch {
    /* noop */
  }
  return sharedAudioCtx;
};

export const createServerTts = (): ElliottTtsEngine => {
  let session = 0;
  let audio: HTMLAudioElement | null = null;
  let objectUrl: string | null = null;
  // Pause/resume for the turn in flight, reaching into speak()'s closure.
  let current: { pause: () => void; resume: () => void } | null = null;

  const cleanup = () => {
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      try {
        audio.pause();
      } catch {
        /* noop */
      }
      audio = null;
    }
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }
  };

  return {
    speak(text, cb) {
      session += 1;
      const mySession = session;
      cleanup();
      current = null;
      // Sentence-sized chunks fetched IN PARALLEL and played in order: the
      // first (short) sentence's audio lands in ~1–2s instead of waiting for
      // the whole turn's MP3, and onStart lets the captions sync to the voice.
      const chunks = splitIntoChunks(text);
      if (!chunks.length) {
        cb.onEnd();
        return;
      }
      const fetches = chunks.map((chunk) => fetchChunk(chunk));
      // A later chunk may still reject after an earlier one already failed
      // the turn; those rejections are expected, not unhandled.
      fetches.forEach((p) => p.catch(() => undefined));
      let index = 0;
      let started = false;
      let voiced = 0;
      let failed = false;
      // Held mid-sentence by pause(). The element keeps its position, so
      // resume() carries on from the exact word.
      let paused = false;
      // A sentence whose audio arrived while paused waits here, unplayed.
      let heldPlayback: (() => void) | null = null;
      let playingAt = 0;
      // Word-synced captions without word timestamps from the API: weight each
      // word by length, map the fractions onto the chunk's REAL playback
      // position (currentTime/duration) every frame. Sentence-level accuracy
      // is exact; intra-sentence is a close estimate.
      const scheduleCaptions = (el: HTMLAudioElement, chunkText: string) => {
        if (!cb.onCaption) return;
        const words = chunkText.split(/\s+/).filter(Boolean);
        if (!words.length) return;
        const weights = words.map((w) => w.length + 1);
        const totalWeight = weights.reduce((a, b) => a + b, 0);
        const startFractions: number[] = [];
        let acc = 0;
        for (const w of weights) {
          startFractions.push(acc / totalWeight);
          acc += w;
        }
        let lastIdx = -1;
        const tick = () => {
          if (mySession !== session || el.ended) return;
          const { duration } = el;
          if (!el.paused && Number.isFinite(duration) && duration > 0) {
            const frac = el.currentTime / duration;
            let idx = 0;
            for (let i = 0; i < startFractions.length; i += 1) if (frac >= startFractions[i]) idx = i;
            if (idx !== lastIdx) {
              lastIdx = idx;
              cb.onCaption?.(words, idx);
            }
          }
          window.requestAnimationFrame(tick);
        };
        window.requestAnimationFrame(tick);
      };
      // Chunk `at` could not be voiced. A request failure before anything was
      // audible counts toward the latch (see above); a playback hiccup never
      // does. Either way the rest of the turn goes to the next engine.
      const fail = (at: number, kind: 'request' | 'playback', err?: any) => {
        if (mySession !== session || failed) return;
        failed = true;
        cleanup();
        if (kind === 'request') {
          if (voiced === 0) silentTurns += 1;
          if (isVoiceUnavailable(err) || silentTurns >= SILENT_TURNS_BEFORE_LATCH) markServerVoiceDown();
        }
        cb.onError({ remaining: remainingText(chunks, at) });
      };
      // Live amplitude for the talking avatar: route the element through an
      // AnalyserNode and stream RMS levels per frame. Wired only when the
      // context is actually running — a suspended context would MUTE playback.
      const attachMeter = (el: HTMLAudioElement) => {
        if (!cb.onLevel) return;
        try {
          const ctx = getAudioCtx();
          if (!ctx || ctx.state !== 'running') return;
          const source = ctx.createMediaElementSource(el);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          source.connect(analyser);
          analyser.connect(ctx.destination);
          const data = new Uint8Array(analyser.fftSize);
          const tick = () => {
            if (mySession !== session || el.ended) {
              cb.onLevel?.(0);
              return;
            }
            if (!el.paused) {
              analyser.getByteTimeDomainData(data);
              let sum = 0;
              for (const sample of data) {
                const v = (sample - 128) / 128;
                sum += v * v;
              }
              cb.onLevel?.(Math.min(1, Math.sqrt(sum / data.length) * 5));
            }
            window.requestAnimationFrame(tick);
          };
          window.requestAnimationFrame(tick);
        } catch {
          /* meter is best-effort — plain playback continues */
        }
      };
      const playNext = () => {
        if (mySession !== session) return;
        if (index >= fetches.length) {
          cleanup();
          cb.onEnd();
          return;
        }
        const at = index;
        const next = fetches[at];
        const chunkText = chunks[at];
        index += 1;
        next
          .then((blob) => {
            if (mySession !== session) return undefined;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            objectUrl = URL.createObjectURL(blob);
            audio = new Audio(objectUrl);
            attachMeter(audio);
            scheduleCaptions(audio, chunkText);
            audio.onplay = () => {
              if (mySession !== session) return;
              voiced += 1;
              silentTurns = 0;
              if (started) return;
              started = true;
              cb.onStart?.();
            };
            audio.onended = () => {
              if (mySession !== session) return;
              playNext();
            };
            audio.onerror = () => fail(at, 'playback');
            const el = audio;
            // A rejected play() (autoplay policy, undecodable audio) is the
            // browser's problem, not the server's.
            const startPlayback = () => {
              if (mySession !== session) return undefined;
              playingAt = at;
              const playing = el.play();
              return playing && typeof playing.catch === 'function'
                ? playing.catch((err: any) => fail(at, 'playback', err))
                : undefined;
            };
            if (paused) {
              heldPlayback = startPlayback;
              return undefined;
            }
            return startPlayback();
          })
          .catch((err: any) => fail(at, 'request', err));
      };
      current = {
        pause: () => {
          if (mySession !== session || failed || paused) return;
          paused = true;
          if (audio && !audio.paused) audio.pause();
        },
        resume: () => {
          if (mySession !== session || failed || !paused) return;
          paused = false;
          if (heldPlayback) {
            const run = heldPlayback;
            heldPlayback = null;
            run();
            return;
          }
          if (audio?.paused && !audio.ended) {
            const el = audio;
            const playing = el.play();
            if (typeof playing?.catch === 'function') {
              playing.catch((err: any) => fail(playingAt, 'playback', err));
            }
          }
        }
      };
      playNext();
    },
    cancel() {
      session += 1;
      current = null;
      cleanup();
    },
    pause() {
      current?.pause();
    },
    resume() {
      current?.resume();
    }
  };
};

// ── Server STT (MediaRecorder + RMS voice-activity meter) ────────────────────

// The meter never ends a recording and never submits: a pause must not send,
// because an accidental submission spends one of the learner's limited
// attempts. It does two jobs. It detects that the learner is speaking
// (adaptive, so it works for quiet speakers and noisy rooms alike: it tracks
// the ambient noise floor and wants two consecutive loud ticks). And it cuts
// the recording into PHRASES at natural breaks, the end of a sentence or a
// breath, transcribing each one the moment it ends so the learner's words land
// on screen while they carry on. The transcription model only takes finished
// audio, so a phrase is as live as this endpoint allows.
const SPEECH_RMS_MIN = 0.012;
const METER_MS = 120;
/** Quiet after speech that ends a phrase: the end of a sentence, a breath. */
export const PHRASE_BREAK_MS = 500;
/** A phrase shorter than this waits for more: tiny clips transcribe badly. */
export const PHRASE_MIN_MS = 1_200;
/** A phrase this long takes the next small dip rather than wait for a break. */
export const PHRASE_LONG_MS = 10_000;
const PHRASE_LONG_BREAK_MS = 240;
// A break also needs this many quiet readings in a row. In a visible tab that
// is always true by the time the quiet above has passed; it matters when the
// browser slows a background tab's timers to one reading a second, where a
// single reading between two words would otherwise look like a full second
// of quiet and cut the sentence in half.
const PHRASE_BREAK_TICKS = 3;
const PHRASE_LONG_BREAK_TICKS = 2;
/** Smaller than this and the clip is a blip, not speech: nothing to upload. */
const MIN_PHRASE_BYTES = 2000;

const normalise = (s: string) =>
  s
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[^a-z0-9' ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The transcription model is given Elliott's last message as a prompt, and on
 * a short clip it can write that prompt back into the transcript. A run of
 * sentences that appears word for word in the prompt is dropped when it adds
 * up to six words or more, so a short phrase the learner happens to share with
 * Elliott ("take your time") survives.
 */
export const stripPromptEcho = (text: string, prompt: string): string => {
  const said = ` ${normalise(prompt)} `;
  if (!said.trim()) return text;
  const sentences = text.match(/[^.!?]+[.!?]*["'\u2019\u201d]*\s*/g) || [text];
  const echoed = sentences.map((sentence) => {
    const words = normalise(sentence);
    return words ? said.includes(` ${words} `) : false;
  });
  const drop = new Set<number>();
  let i = 0;
  while (i < sentences.length) {
    if (!echoed[i]) {
      i += 1;
      continue;
    }
    let j = i;
    let words = 0;
    while (j < sentences.length && echoed[j]) {
      words += normalise(sentences[j]).split(' ').length;
      j += 1;
    }
    if (words >= 6) for (let k = i; k < j; k += 1) drop.add(k);
    i = j;
  }
  return sentences
    .filter((_, k) => !drop.has(k))
    .join('')
    .trim();
};

/** One phrase's transcript: one retry, and '' if it is lost. */
const transcribePhrase = (blob: Blob, mime: string, context: string): Promise<string> =>
  new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = () => resolve('');
    reader.readAsDataURL(blob);
  }).then((audioBase64) => {
    if (!audioBase64) return '';
    const attempt = (n: number): Promise<string> =>
      API.elliottVoiceStt({ audioBase64, mime, context })
        .then((res: any) => stripPromptEcho(String(res?.data?.text || '').trim(), context))
        .catch((err: any) => {
          if (n < 1 && !isVoiceUnavailable(err)) return attempt(n + 1);
          // Transcription lost: the next recording uses the browser engine.
          markServerVoiceDown();
          return '';
        });
    return attempt(0);
  });

interface Phrase {
  recorder: MediaRecorder;
  chunks: Blob[];
  startedAt: number;
  heardSpeech: boolean;
}

export const createServerStt = (getContext?: () => string): ElliottSttEngine | null => {
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') return null;

  let active = false;
  let discard = false;
  let cb: ElliottSttCallbacks | null = null;
  let stream: MediaStream | null = null;
  let audioCtx: AudioContext | null = null;
  let meterTimer: number | null = null;
  let mimeType = '';
  // The phrase being recorded right now.
  let phrase: Phrase | null = null;
  let recordersOpen = 0;
  // Phrases are numbered as they end and handed over strictly in that order,
  // whichever transcription comes back first.
  let ended = 0;
  let handedOver = 0;
  const written = new Map<number, string>();
  // Phrases held for onFinal when the caller takes no onPhrase.
  let banked = '';
  let pending = false;
  let heardAny = false;

  const stopMeter = () => {
    if (meterTimer !== null) {
      window.clearInterval(meterTimer);
      meterTimer = null;
    }
  };

  const releaseMedia = () => {
    stopMeter();
    if (audioCtx) {
      try {
        audioCtx.close();
      } catch {
        /* noop */
      }
      audioCtx = null;
    }
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
  };

  const reportPending = () => {
    const next = Boolean(phrase?.heardSpeech) || ended > handedOver;
    if (next === pending) return;
    pending = next;
    cb?.onPending?.(next);
  };

  // Elliott's last turn biases the model toward the scenario's domain and cuts
  // hallucinations on short clips. Nothing else goes in: transcript-like text
  // in the prompt is what the model is most likely to echo back.
  const context = () => getContext?.() || '';

  const handOver = () => {
    if (discard || !cb) return;
    while (written.has(handedOver)) {
      const text = written.get(handedOver) || '';
      written.delete(handedOver);
      handedOver += 1;
      if (!text) continue;
      if (cb.onPhrase) cb.onPhrase(text);
      else banked = `${banked} ${text}`.trim();
    }
    reportPending();
    // Stopped, and the last phrase is written down: the recording is over.
    if (!active && !phrase && handedOver === ended) {
      const finalCb = cb;
      cb = null;
      finalCb.onFinal(banked);
    }
  };

  const beginPhrase = (s: MediaStream): Phrase => {
    const recorder = mimeType ? new MediaRecorder(s, { mimeType }) : new MediaRecorder(s);
    const next: Phrase = { recorder, chunks: [], startedAt: Date.now(), heardSpeech: false };
    recorder.ondataavailable = (e: any) => {
      if (e.data?.size) next.chunks.push(e.data);
    };
    recorder.start(250);
    recordersOpen += 1;
    return next;
  };

  const endPhrase = (keepRecording: boolean) => {
    const done = phrase;
    // The next phrase starts before this one stops, so no word falls between.
    phrase = keepRecording && stream ? beginPhrase(stream) : null;
    if (!done) {
      handOver();
      return;
    }
    const index = ended;
    ended += 1;
    const { recorder } = done;
    const finish = (text: string) => {
      written.set(index, text);
      handOver();
    };
    const closed = () => {
      recordersOpen -= 1;
      if (!active && recordersOpen === 0) releaseMedia();
    };
    reportPending();
    if (recorder.state === 'inactive') {
      closed();
      finish('');
      return;
    }
    recorder.onstop = () => {
      closed();
      if (discard) return;
      const mime = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(done.chunks, { type: mime });
      if (!done.heardSpeech || blob.size < MIN_PHRASE_BYTES) {
        finish('');
        return;
      }
      transcribePhrase(blob, mime, context()).then(finish);
    };
    try {
      recorder.stop();
    } catch {
      recorder.onstop = null;
      closed();
      finish('');
    }
  };

  return {
    start(callbacks) {
      active = true;
      discard = false;
      cb = callbacks;
      navigator.mediaDevices
        .getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
        .then((s) => {
          if (!active) {
            s.getTracks().forEach((t) => t.stop());
            return;
          }
          stream = s;
          mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : MediaRecorder.isTypeSupported('audio/mp4')
            ? 'audio/mp4'
            : '';
          phrase = beginPhrase(s);

          const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
          audioCtx = new AC();
          const source = audioCtx!.createMediaStreamSource(s);
          const analyser = audioCtx!.createAnalyser();
          analyser.fftSize = 512;
          source.connect(analyser);
          const buf = new Uint8Array(analyser.fftSize);
          let noiseFloor = 0.003;
          let speechTicks = 0;
          let quietTicks = 0;
          let lastSpeechAt = 0;
          let lastActivityAt = 0;
          meterTimer = window.setInterval(() => {
            if (!active || !phrase) return;
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (const sample of buf) {
              const v = (sample - 128) / 128;
              sum += v * v;
            }
            const rms = Math.sqrt(sum / buf.length);
            // Track the room's noise floor while nobody is speaking (slow EMA,
            // capped so shouting rooms can't push the floor into speech range).
            if (!heardAny) noiseFloor = Math.min(0.02, noiseFloor * 0.95 + rms * 0.05);
            const speechThreshold = Math.max(SPEECH_RMS_MIN, noiseFloor * 3);
            const now = Date.now();
            if (rms > speechThreshold) {
              speechTicks += 1;
              quietTicks = 0;
              // Any loud tick keeps a phrase that has started alive.
              if (phrase.heardSpeech) lastSpeechAt = now;
              // ~240ms sustained = real speech, not a blip.
              if (speechTicks >= 2) {
                lastSpeechAt = now;
                if (!phrase.heardSpeech) {
                  phrase.heardSpeech = true;
                  reportPending();
                }
                if (!heardAny) {
                  heardAny = true;
                  cb?.onSpeechStart?.();
                }
                // Keeps the idle guard from closing the mic mid-answer.
                if (now - lastActivityAt > 1000) {
                  lastActivityAt = now;
                  cb?.onActivity?.();
                }
              }
              return;
            }
            speechTicks = 0;
            quietTicks += 1;
            if (!phrase.heardSpeech) return;
            const quiet = now - lastSpeechAt;
            const length = now - phrase.startedAt;
            if (
              (quiet >= PHRASE_BREAK_MS && quietTicks >= PHRASE_BREAK_TICKS && length >= PHRASE_MIN_MS) ||
              (length >= PHRASE_LONG_MS && quiet >= PHRASE_LONG_BREAK_MS && quietTicks >= PHRASE_LONG_BREAK_TICKS)
            ) {
              endPhrase(true);
            }
          }, METER_MS);
        })
        .catch((err: any) => {
          if (!active) return;
          active = false;
          releaseMedia();
          const finalCb = cb;
          cb = null;
          const name = err?.name || '';
          if (name === 'NotAllowedError' || name === 'SecurityError') finalCb?.onPermissionDenied();
          else finalCb?.onUnavailable();
        });
    },
    stop() {
      if (!active) return;
      active = false;
      stopMeter();
      endPhrase(false);
      if (recordersOpen === 0) releaseMedia();
    },
    abort() {
      discard = true;
      active = false;
      cb = null;
      stopMeter();
      const open = phrase;
      phrase = null;
      if (open && open.recorder.state !== 'inactive') {
        try {
          open.recorder.stop();
        } catch {
          /* noop */
        }
      }
      releaseMedia();
    }
  };
};
